import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import equal from 'fast-deep-equal';
import { cn, Price } from '@divlab/divanui';
import { IconCart } from '@divlab/divanui/icons';
import { CSSTransition } from 'react-transition-group';

import * as PageProductStore from '@Stores/PageProduct';
import * as ModalWindows from '@Stores/ModalWindows';
import useTranslation from '@Queries/useTranslation';
import Button from '@UI/Button';
import styles from './StickyBuyButton.module.css';

import type { ButtonProps } from '@UI/Button';
import type { FC } from 'react';

export interface StickyBuyButtonProps extends ButtonProps {
  className?: string;
  visible?: boolean;
}

const StickyBuyButton: FC<StickyBuyButtonProps> = (props) => {
  const { className, visible = false, ...restProps } = props;
  const page = PageProductStore.useData();
  const { t } = useTranslation();
  const parameterValues = PageProductStore.getParameterValues();
  const defaultParameterValues = useRef(parameterValues);
  const [open, setOpen] = useState(false);

  const isModifiedParameters = useMemo(() => {
    return !equal(defaultParameterValues.current, parameterValues);
  }, [parameterValues, defaultParameterValues]);

  const handleClick = useCallback(() => {
    ModalWindows.open('Cart', {
      products: [
        {
          isModular: page.isModular,
          shopProductId: page.product.id,
          parameterValues,
          isModifiedParameters,
        },
      ],
      analyticsLabel: 'Карточка товара',
    });
  }, [page.isModular, page.product.id, parameterValues, isModifiedParameters]);

  useEffect(() => {
    setOpen(visible);
  }, [visible]);

  return (
    <CSSTransition
      timeout={400}
      classNames={{
        enter: styles.enter,
        enterActive: styles.enterActive,
        enterDone: styles.enterDone,
        exit: styles.exit,
        exitActive: styles.exitActive,
        exitDone: styles.exitDone,
      }}
      unmountOnExit
      in={open}
    >
      <Button
        {...restProps}
        wide
        size='l'
        className={cn(styles.buyButton, styles.sizeBig, className)}
        onClick={handleClick}
        data-testid='mobile-buy-button'
      >
        <IconCart className={styles.iconBasket} />
        {t('ui.add-to-cart')}
        <Price className={styles.price} price={page.product.price.actual} />
      </Button>
    </CSSTransition>
  );
};

export default memo(StickyBuyButton);
